/* import __COLOCATED_TEMPLATE__ from './phone-runbook.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type AppService from 'embercom/services/app-service';
import type {
  FinVoiceRunbook,
  FinVoiceProcedure,
  WorkflowSelection,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import { getLabelForRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { get } from 'embercom/lib/ajax';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    phoneNumberId?: string; // Optional - kept for compatibility but not used
    numberConfiguration?: FinVoiceNumberConfiguration;
    descriptionText?: string;
    updateRunbook: (runbook: FinVoiceRunbook, procedureId: string) => Promise<void>;
  };
}

export default class PhoneRunbook extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;

  @tracked procedures: FinVoiceProcedure[] = [];
  @tracked isLoadingProcedures = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadProcedures();
  }

  get currentWorkflowSelection(): WorkflowSelection {
    // Check if a procedure is saved
    if (this.savedProcedureId) {
      return {
        type: 'procedure',
        procedureId: this.savedProcedureId,
      };
    }

    // Default to runbook
    return {
      type: 'runbook',
      runbook: this.savedRunbook,
    };
  }

  async loadProcedures() {
    if (!this.appService.app.canUseFeature('fin-voice-procedures')) {
      return;
    }

    this.isLoadingProcedures = true;
    try {
      let response = await get('/ember/fin_voice/procedures', {
        app_id: this.appService.app.id,
        state: 'live',
      });
      this.procedures = response as FinVoiceProcedure[];
    } catch (e: unknown) {
      this.procedures = [];
      this.notificationsService.notifyError(
        this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.runbook.error-loading-procedures',
        ),
      );
    } finally {
      this.isLoadingProcedures = false;
    }
  }

  get savingDisabled() {
    if (this.numberConfiguration?.isSaving) {
      return true;
    }

    // Check if workflow selection has changed
    if (!this.pendingSelection) {
      return true;
    }

    if (this.pendingSelection.type === 'procedure') {
      return this.pendingSelection.procedureId === this.savedProcedureId;
    } else {
      // If switching from a saved procedure to a runbook, that's always a change
      if (this.savedProcedureId) {
        return false;
      }
      return this.pendingSelection.runbook === this.savedRunbook;
    }
  }

  get errorMessage() {
    if (!this.savingDisabled) {
      return undefined;
    }

    if (this.numberConfiguration?.isSaving) {
      return undefined;
    }

    return this.intl.t('operator.fin.setup.tabs.setup-and-go-live.phone.runbook.error-no-changes');
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedRunbook() {
    return this.numberConfiguration?.runbook || 'none';
  }

  get savedProcedureId() {
    return this.numberConfiguration?.handoverConfiguration?.procedureId || '';
  }

  get savedRunbookName() {
    if (this.savedProcedureId) {
      let procedure = this.procedures.find((p) => p.id === this.savedProcedureId);
      if (procedure) {
        return procedure.name;
      }
    }

    return this.intl.t(
      getLabelForRunbook(this.savedRunbook) ||
        'operator.fin.setup.tabs.setup-and-go-live.phone.runbook.not-set-up',
    );
  }

  get isLoadingSavedProcedure() {
    return this.savedProcedureId && this.isLoadingProcedures;
  }

  @tracked private pendingSelection?: WorkflowSelection;

  @action
  resetRunbook() {
    this.pendingSelection = undefined;
  }

  @action
  onWorkflowChange(selection: WorkflowSelection) {
    this.pendingSelection = selection;
  }

  @action
  async updateRunbook() {
    if (!this.pendingSelection) {
      return;
    }

    let runbook: FinVoiceRunbook;
    let procedureId: string;

    if (this.pendingSelection.type === 'procedure') {
      runbook = 'none';
      procedureId = this.pendingSelection.procedureId || '';
    } else {
      runbook = this.pendingSelection.runbook || 'none';
      procedureId = '';
    }

    await this.args.updateRunbook(runbook, procedureId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::PhoneRunbook': typeof PhoneRunbook;
  }
}
